import type { Match } from "navigo";
import type { ChildDom } from "vanjs-core";
import { reactive } from "vanjs-ext";
import { errorSection } from "../error-section";
import { loadingScreen } from "../loading-screen";
import { t } from "../locale";
import type { Route, RouteContext } from "../router";
import { state } from "../state";
import { userPlaceholder } from "../user/user";
import { centeredBox } from "../van/centered-box";
import { button, div, h1, input, label, option, select } from "../van/van-tags";

export class AccountPage implements Route {
	async #saveChanges(): Promise<void> {
		const oldLocale = this.#reactive.user.Locale;
		const req = this.#ctx.api.put(`/users/${this.#reactive.user.Id}`);
		req.setJsonBody({
			Email: this.#reactive.user.Email,
			Phone: this.#reactive.user.Phone,
			Locale: this.#reactive.user.Locale,
			EmailNotifications: {
				Billing: this.#reactive.user.EmailNotifications.Billing,
				Device: this.#reactive.user.EmailNotifications.Device,
				Marketing: this.#reactive.user.EmailNotifications.Marketing,
			},
			PushNotifications: this.#reactive.user.PushNotifications.Enabled,
			SmsNotifications: this.#reactive.user.SmsNotifications.Enabled,
		});
		req.setSignedInUserAuth();

		errorSection.clear();
		const res = await loadingScreen.waitFor(req.do());
		if (res.ok) {
			const user = await res.json();
			state.setUser(user);
			this.#reactive.user = user;
			if (user.Locale !== oldLocale) location.reload();
		} else {
			errorSection.setText(await res.text());
		}
	}

	async refresh(_: Match): Promise<void> {
		this.#reactive.user = await state.getActiveUser();
	}

	#ctx;
	#reactive = reactive({
		user: userPlaceholder,
	});

	ChildDom: ChildDom;

	constructor(ctx: RouteContext) {
		this.#ctx = ctx;
		this.ChildDom = centeredBox(
			{ size: "narrow" },
			h1({ class: "title" }, () => t.account),
			div(
				{ class: "field" },
				label({ class: "label" }, () => t.email),
				div(
					{ class: "control" },
					input({
						class: "input",
						oninput: (e: Event) => {
							if (!(e.target instanceof HTMLInputElement)) return;
							this.#reactive.user.Email = e.target.value;
						},
						value: () => this.#reactive.user.Email,
					}),
				),
			),
			div(
				{ class: "field" },
				label({ class: "label" }, () => t.phone),
				div(
					{ class: "control" },
					input({
						class: "input",
						oninput: (e: Event) => {
							if (!(e.target instanceof HTMLInputElement)) return;
							this.#reactive.user.Phone = e.target.value;
						},
						value: () => this.#reactive.user.Phone,
					}),
				),
			),
			div(
				{ class: "field" },
				label({ class: "label" }, () => t.language),
				div(
					{ class: "control" },
					div(
						{ class: "select" },
						select(
							{
								onchange: (e: Event) => {
									if (
										!(e.target instanceof HTMLSelectElement)
									)
										return;
									switch (e.target?.value) {
										case "de-DE":
										case "en-GB":
										case "fr-FR":
										case "sv-SE":
											break;
										default:
											return;
									}
									this.#reactive.user.Locale = e.target.value;
								},
							},
							[
								["de-DE", "Deutsch"],
								["en-GB", "English"],
								["fr-FR", "Français"],
								["sv-SE", "Svenska"],
							].map((lang) =>
								option(
									{
										selected: () =>
											this.#reactive.user.Locale ===
											lang[0],
										value: lang[0],
									},
									lang[1],
								),
							),
						),
					),
				),
			),
			div(
				{ class: "field" },
				label({ class: "label" }, t.notifications),
				div(
					{ class: "control" },
					label(
						{ class: "checkbox" },
						input({
							checked: () =>
								this.#reactive.user.EmailNotifications.Device,
							onchange: (e: Event) => {
								if (!(e.target instanceof HTMLInputElement))
									return;
								this.#reactive.user.EmailNotifications.Device =
									e.target.checked;
							},
							type: "checkbox",
						}),
						// deliberate initial space due to formatting
						() => ` ${t.email}`,
					),
				),
				div(
					{ class: "control" },
					label(
						{ class: "checkbox" },
						input({
							checked: () =>
								this.#reactive.user.PushNotifications.Enabled,
							onchange: (e: Event) => {
								if (!(e.target instanceof HTMLInputElement))
									return;
								this.#reactive.user.PushNotifications.Enabled =
									e.target.checked;
							},
							type: "checkbox",
						}),
						// deliberate initial space due to formatting
						" Push",
					),
				),
				div(
					{ class: "control" },
					label(
						{ class: "checkbox" },
						input({
							checked: () =>
								this.#reactive.user.SmsNotifications.Balance >
									0 &&
								this.#reactive.user.SmsNotifications.Enabled,
							disabled: () =>
								this.#reactive.user.SmsNotifications.Balance <
								1,
							onchange: (e: Event) => {
								if (!(e.target instanceof HTMLInputElement))
									return;
								this.#reactive.user.SmsNotifications.Enabled =
									e.target.checked;
							},
							type: "checkbox",
						}),
						// deliberate initial space due to formatting
						() =>
							` SMS - ${this.#reactive.user.SmsNotifications.Balance}`,
					),
				),
				div(
					{ class: "control" },
					button(
						{
							class: "button is-text",
							onclick: () =>
								window.open(
									t.newsletterUrl,
									"_blank",
									"noopener,noreferrer",
								),
						},
						() => t.newsletter,
					),
				),
			),
			div(
				{ class: "field is-grouped is-grouped-multiline" },
				div(
					{ class: "control" },
					button(
						{
							class: "button is-primary",
							onclick: () => this.#saveChanges(),
						},
						() => t.save,
					),
				),
				div(
					{ class: "control" },
					button(
						{
							class: "button is-primary",
							onclick: () => {
								this.#ctx.router.jump("/account/buy-sms");
							},
						},
						() => t.buySms,
					),
				),
				div(
					{ class: "control" },
					button(
						{
							class: "button is-danger",
							onclick: () => {
								this.#ctx.router.jump("/account/delete");
							},
						},
						() => t.deleteAccount,
					),
				),
			),
		);
	}
}
